import React, { useContext, useEffect, useMemo, useState } from "react";
import { usePrevious } from "@shared/utils/hooks/misc";
import { useSelector } from "react-redux";
import { closedTradesFilters } from "@shared/slices/filters.slice.ts";
import { serializeFilters } from "@shared/features/filters/utils.tsx";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions.ts";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context.tsx";
import useTradesGraphData from "features/trades/TradesCharts/useTradesGraphData.ts";
import { PhoneHeroCollapse } from "features/dashboard/PhoneDashboard/PhoneHeroControls.tsx";
import PhoneClosedPnlChart, {
  ClosedPnlChartToggle,
  ClosedPnlSpark,
} from "features/trades/NewLook/PhoneClosedPnlChart.tsx";

export type ClosedPnlGraph = ReturnType<typeof useClosedPnlGraph>;

const useClosedPnlGraph = () => {
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();
  const filtersState = useSelector(closedTradesFilters.selectors.selectFilters);
  const filters = useMemo(() => serializeFilters(filtersState), [filtersState]);
  const graph = useTradesGraphData({ filters, precision });
  const { getTradesQuery } = useContext(TradesPageContext) || {};
  const { data, refetch } = graph;
  const isTradesFetching = !!getTradesQuery?.isFetching;
  const wasTradesFetching = usePrevious(isTradesFetching);
  useEffect(() => {
    if (isTradesFetching && !wasTradesFetching && data) refetch();
  }, [isTradesFetching, wasTradesFetching, data, refetch]);
  return { ...graph, precision };
};

const useClosedPnlChartSlots = () => {
  const graph = useClosedPnlGraph();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const toggle = () => {
    setMounted(true);
    setOpen((value) => !value);
  };
  return {
    onToggle: toggle,
    aside: <ClosedPnlSpark graph={graph} hidden={open} />,
    metaAction: <ClosedPnlChartToggle open={open} onToggle={toggle} />,
    children: (
      <PhoneHeroCollapse open={open}>
        {mounted && <PhoneClosedPnlChart graph={graph} />}
      </PhoneHeroCollapse>
    ),
  };
};

export default useClosedPnlChartSlots;
